import Divider from "components/Divider/Divider";
import React from "react";
import s from "./PaymentDetailSection.module.scss";
import { checkMoneyDataForTable, getCardTypeIcon, dateTimeFormatter } from "utils/utils";
import { useTranslation } from "react-i18next";
import TextMAS from "components/TextMAS/TextMAS";
import { Typography } from "@mui/material";
import PaymentDetailsGrid from "../payment-detail-grid/PaymentDetailsGrid";
import PaymentDetailTag from "../payment-detail-tag/PaymentDetailTag";

const cc = require("currency-codes");

const PaymentDetailSection = ({ offline, online, payment = {} }) => {
  const isOffline = offline || !online;
  const ENV_LOCALE_KEY = isOffline ? "offline" : "online";
  const COMPONENT_LOCALE_KEY = `page.transactions.sharedComponents.paymentDetailSection.${ENV_LOCALE_KEY}.`;

  const { t } = useTranslation();

  const getCardIconComponent = (payment, styles = {}) => {
    const getCardType = (payment, isOffline) => {
      if (isOffline) {
        return payment?.cardType ?? "";
      }
      return payment?.card_type ?? "";
    };

    const cardType = getCardType(payment, isOffline);
    const cardIcon = getCardTypeIcon(cardType) ?? "";
    return cardIcon ? (
      <div>
        <img
          src={cardIcon}
          style={{ verticalAlign: "bottom", ...styles }}
          height={24}
          alt={cardType.toUpperCase()}
        />
      </div>
    ) : (
      "-"
    );
  };

  const getPaymentDetailsDescriptionOffline = () => {
    try {
      return payment?.description
        ? payment?.gateway?.startsWith("OCTOPUS")
          ? "{" +
          "merchantRef : " +
          JSON.parse(payment?.description).merchantRef +
          ", " +
          "invoiceNo : " +
          JSON.parse(payment?.description).invoiceNo +
          "}"
          : payment?.description
        : "-";
    } catch (e) {
      return payment?.description ? payment?.description : "-";
    }
  };

  const paymentDetailsMainContent = () => {
    if (isOffline) {
      //========================== Offline ==========================
      return (
        <div className={s["header-info-container"]}>
          <div>
            {getCardIconComponent(payment) !== "-"
              ? getCardIconComponent(payment, {
                width: 72,
                height: 48,
              })
              : payment?.cardType}
            <div>
              <Typography variant="content-l-semibold">
                {payment?.currency &&
                  (cc.number(payment?.currency)?.code ?? "")}
              </Typography>
              <Typography variant="h1">
                {checkMoneyDataForTable(payment, "netAmount")}
              </Typography>
            </div>
          </div>
          <PaymentDetailTag status={payment.status} />
        </div>
      );
    } else {
      //========================== Online ==========================
      return (
        <div className={s["header-info-container"]}>
          <div>
            {getCardIconComponent(payment) !== "-"
              ? getCardIconComponent(payment, {
                width: 72,
                height: 48,
              })
              : payment?.cardType}
            <div>
              <Typography variant="content-l-semibold">
                {payment?.currency && (payment?.currency ?? "")}
              </Typography>
              <Typography variant="h1">
                {checkMoneyDataForTable(payment, "net_amount")}
              </Typography>
            </div>
          </div>
          <PaymentDetailTag status={payment.status} />
        </div>
      );
    }
  };

  const paymentDetailsContent = () => {
    if (isOffline) {
      //========================== Offline ==========================
      return (
        <>
          <PaymentDetailsGrid
            items={[
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.category`),
                value: payment?.category ? `${payment?.category}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.paymentId`),
                value: payment?.id ? `${payment?.id}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.discount`),
                value: `${cc.number(payment?.currency)?.code ?? ""
                  } ${checkMoneyDataForTable(payment, "discount")} ${payment?.campaignId && payment?.campaignName ? `(${payment?.campaignName})` : ""}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.total`),
                value: `${cc.number(payment?.currency)?.code ?? ""
                  } ${checkMoneyDataForTable(payment, "total")}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.tips`),
                value: `${cc.number(payment?.currency)?.code ?? ""
                  } ${checkMoneyDataForTable(payment, "tips")}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.smallTicketFee`),
                value: `${cc.number(payment?.currency)?.code ?? ""
                  } ${checkMoneyDataForTable(payment, "trans_fee")}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.merchantId`),
                value: payment?.merchantId ? `${payment.merchantId}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.employeeId`),
                value: payment?.employeeId ? `${payment.employeeId}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.mid`),
                value: payment?.terminalMid ? `${payment?.terminalMid}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.tid`),
                value: payment?.terminalTid ? `${payment?.terminalTid}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.terminalSn`),
                value: payment?.terminalSn ? `${payment?.terminalSn}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.paymentGateway`),
                value: payment?.terminalSn ? `${payment?.gateway}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.description`),
                value: getPaymentDetailsDescriptionOffline(),
              },
            ]}
          />
          <PaymentDetailsGrid
            items={[
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.created`),
                value: dateTimeFormatter(payment?.created),
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.updated`),
                value: dateTimeFormatter(payment?.updated),
              },
            ]}
          />
        </>
      );
    } else {
      //========================== Online ==========================
      return (
        <>
          <PaymentDetailsGrid
            items={[
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.orderId`),
                value: payment?.id ? `${payment.id}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.foreignCardFee`),
                value: `${payment?.currency ?? ""} ${checkMoneyDataForTable(
                  payment,
                  "change_fee",
                )}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.discount`),
                value: `${payment?.currency ?? ""} ${checkMoneyDataForTable(
                  payment,
                  "discount_amount",
                )}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.amount`),
                value: `${payment?.currency ?? ""} ${checkMoneyDataForTable(
                  payment,
                  "amount",
                )}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.itemAmount`),
                value: `${payment?.currency ?? ""} ${checkMoneyDataForTable(
                  payment,
                  "item_amount",
                )}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.totalAuthorizedAmount`),
                value: `${payment?.currency ?? ""} ${checkMoneyDataForTable(
                  payment,
                  "total_authorized_amount",
                )}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.totalRefundedAmount`),
                value: `${payment?.currency ?? ""} ${checkMoneyDataForTable(
                  payment,
                  "total_refunded_amount",
                )}`,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.merchantId`),
                value: payment?.merchantId ? `${payment.merchantId}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.merchantRef`),
                value: payment?.merchantRef ? `${payment.merchantRef}` : "",
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.paymentGateway`),
                value: payment?.payment_gateway
                  ? `${payment.payment_gateway}`
                  : "",
              },
            ]}
          />
          <PaymentDetailsGrid
            items={[
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.created`),
                value: dateTimeFormatter(payment?.created),
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.updated`),
                value: dateTimeFormatter(payment?.updated),
              },
            ]}
          />
          <PaymentDetailsGrid
            items={[
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.cardOrigin`),
                value: payment?.card_origin,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.cardIssuingCountryCode`),
                value: payment?.issuer_country_code,
              },
              {
                key: t(`${COMPONENT_LOCALE_KEY}content.cardIssuingBank`),
                value: payment?.card_issuer,
              },
            ]}
          />
        </>
      );
    }
  };

  return (
    <div className={s["payment-details"]}>
      <div className={s["header"]}>
        <TextMAS fontSize="24px" fontWeight="medium" color="brandDark64">
          {t(COMPONENT_LOCALE_KEY + "title")}
        </TextMAS>
      </div>
      <Divider />
      <div className={s["content-container"]}>
        {paymentDetailsMainContent()}
        <div className={s["content-2"]}>{paymentDetailsContent()}</div>
      </div>
    </div>
  );
};

export default PaymentDetailSection;
